import type {CmLogger} from "@cm/log"
import {DownloadQueue} from "#utils/download-queue"

export type TileDownloadFn = (url: string, priority?: number, abortSignal?: AbortSignal) => Promise<ArrayBuffer>

export class TiledImageDataCache {
    constructor(
        private logger: CmLogger,
        private cacheInMemory: boolean,
        downloadFn?: TileDownloadFn,
    ) {
        this._downloadFn = downloadFn ?? ((url, priority, abortSignal) => this._downloadQueue.download(url, priority, abortSignal))
    }

    async getRawTileData(url: string, options?: GetRawTileDataOptions): Promise<ImageTileRawData> {
        const priority = options?.priority ?? 0
        let tileData = this._tileDataByUrl.get(url)
        if (!tileData) {
            const start = performance.now()
            const promisedTileData = this.downloadRawData(url, priority, options?.abortSignal).then((encodedData): ImageTileRawData => ({encodedData}))
            this._tileDataByUrl.set(url, promisedTileData)
            try {
                tileData = await promisedTileData
            } catch (error) {
                this._tileDataByUrl.delete(url)
                throw error
            }
            if (this.cacheInMemory) {
                this._tileDataByUrl.set(url, tileData)
            } else {
                this._tileDataByUrl.delete(url)
            }
            this.logger.trace(`TiledImageDataCache: loaded tile from ${url} in ${performance.now() - start} ms`)
        }
        return tileData
    }

    private downloadRawData(url: string, priority?: number, abortSignal?: AbortSignal): Promise<ArrayBuffer> {
        return this._downloadFn(url, priority, abortSignal)
    }

    private readonly _downloadFn: TileDownloadFn
    private _downloadQueue = new DownloadQueue(16)
    private _tileDataByUrl = new Map<string, ImageTileRawData | Promise<ImageTileRawData>>()
}

export type ImageTileRawData = {
    encodedData: ArrayBuffer
}

export type GetRawTileDataOptions = {
    priority?: number // default: 0
    abortSignal?: AbortSignal
}
